import React, { useState, useEffect } from 'react';

const CostStructurePage = ({ sales }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredSales, setFilteredSales] = useState([]);
  const [recipes, setRecipes] = useState([]);

  const [monthlyGrossSales, setMonthlyGrossSales] = useState(0);
  const [averageRecipeCostPercentage, setAverageRecipeCostPercentage] = useState(0);
  const [costStructure, setCostStructure] = useState([]);

  const [reports, setReports] = useState([]);

  // ===============================
  // CARGAR RECETAS
  // ===============================
  const loadRecipes = async () => {
    try {
      const response = await fetch("/api/list_recipes.php");
      const result = await response.json();
      if (result.success) {
        setRecipes(result.data || []);
      } else {
        console.error("Error al cargar recetas:", result.error);
      }
    } catch (error) {
      console.error("Error de conexión al cargar recetas:", error);
    }
  };

  // ===============================
  // CARGAR REPORTES
  // ===============================
  const loadReports = async () => {
    try {
      const response = await fetch('/api/get_cost_structure_reports.php');
      const data = await response.json();
      if (data.success) setReports(data.reports);
    } catch (error) {
      console.error("Error cargando reportes:", error);
    }
  };

  useEffect(() => {
    loadRecipes();
    loadReports();
  }, []);

  // ===============================
  // FILTRO DE FECHAS
  // ===============================
  useEffect(() => {
    let currentFilteredSales = Array.isArray(sales) ? sales : [];

    if (startDate) {
      const startTimestamp = new Date(startDate + "T00:00:00").getTime();
      currentFilteredSales = currentFilteredSales.filter(sale => {
        if (!sale.sale_date) return false;
        const saleTimestamp = new Date(String(sale.sale_date).replace(" ", "T")).getTime();
        return saleTimestamp >= startTimestamp;
      });
    }

    if (endDate) {
      const endTimestamp = new Date(endDate + "T23:59:59").getTime();
      currentFilteredSales = currentFilteredSales.filter(sale => {
        if (!sale.sale_date) return false;
        const saleTimestamp = new Date(String(sale.sale_date).replace(" ", "T")).getTime();
        return saleTimestamp <= endTimestamp;
      });
    }

    setFilteredSales(currentFilteredSales);
  }, [sales, startDate, endDate]);

  // ===============================
  // CALCULO REAL DE CMV
  // ===============================
  useEffect(() => {

    // =========================
    // TOTAL VENTAS (USA TOTAL YA QUE ASÍ LO TIENES)
    // =========================
    const totalSalesWithoutVAT = filteredSales.reduce(
      (total, sale) => total + (Number(sale.total) || 0),
      0
    );

    setMonthlyGrossSales(totalSalesWithoutVAT);

    // =========================
    // CMV REAL (🔥 FIX CLAVE)
    // =========================
    const totalCMV = filteredSales.reduce((sum, sale) => {

      const qty = Number(sale.quantitySold || sale.quantity_sold || 0);

      let recipe = recipes.find(r =>
        Number(r.productId) === Number(sale.product_id)
      );

      if (!recipe) {
        recipe = recipes.find(r =>
          r.name?.toLowerCase().includes(sale.name?.toLowerCase())
        );
      }

      if (!recipe) {
        console.warn("❌ SIN RECETA:", sale.name);
        return sum;
      }

      const costPerUnit =
        Number(recipe.costPerServingCalculated || 0);

      return sum + (costPerUnit * qty);

    }, 0);

    // =========================
    // % REAL (NO PROMEDIO)
    // =========================
    const calculatedAverageRecipeCostPercentage =
      totalSalesWithoutVAT > 0
        ? (totalCMV / totalSalesWithoutVAT) * 100
        : 0;

    setAverageRecipeCostPercentage(calculatedAverageRecipeCostPercentage);

    // =========================
    // ESTRUCTURA
    // =========================
    const detailedCostStructure = [
      { name: 'Costo de la Mercancía Vendida (CMV)', value: totalCMV, reference: '30-35%' },
      { name: 'Salario Personal Operativo', value: 0, reference: '15-20%' },
      { name: 'Salario Personal Administrativo', value: 0, reference: '5-10%' },
      { name: 'Arrendamiento', value: 0, reference: '5-10%' },
      { name: 'Servicios públicos', value: 0, reference: '3-5%' },
      { name: 'Suministros y Gastos Generales', value: 0, reference: '1-3%' },
      { name: 'Publicidad', value: 0, reference: '3-5%' },
      { name: 'Honorarios Profesionales', value: 0, reference: '1-3%' },
      { name: 'Seguros y Licencias', value: 0, reference: '1-2%' },
      { name: 'Mantenimiento y Reparaciones', value: 0, reference: '1-3%' },
      { name: 'Gastos Financieros', value: 0, reference: '1-3%' },
    ];

    const totalCosts = detailedCostStructure.reduce((sum, item) => sum + item.value, 0);
    const netProfit = totalSalesWithoutVAT - totalCosts;

    const finalStructure = [
      ...detailedCostStructure,
      { name: 'Utilidad Neta', value: netProfit, reference: '10-20%' }
    ];

    const calculatedStructure = finalStructure.map(item => ({
      ...item,
      percentage: totalSalesWithoutVAT > 0
        ? (item.value / totalSalesWithoutVAT) * 100
        : 0
    }));

    setCostStructure(calculatedStructure);

  }, [filteredSales, recipes]);

  // ===============================
  // HANDLERS
  // ===============================
  const handleCostValueChange = (index, value) => {
    const updatedStructure = [...costStructure];

    if (
      updatedStructure[index].name !== 'Costo de la Mercancía Vendida (CMV)' &&
      updatedStructure[index].name !== 'Utilidad Neta'
    ) {
      updatedStructure[index].value = Number(value) || 0;

      const totalCosts = updatedStructure.reduce((sum, item) => {
        if (item.name !== 'Costo de la Mercancía Vendida (CMV)' && item.name !== 'Utilidad Neta')
          return sum + item.value;
        return sum;
      }, 0);

      const cmvValue =
        updatedStructure.find(item => item.name === 'Costo de la Mercancía Vendida (CMV)')?.value || 0;

      const netProfit = monthlyGrossSales - totalCosts - cmvValue;

      const recalculatedStructure = updatedStructure.map(item => {
        if (item.name === 'Utilidad Neta')
          return { ...item, value: netProfit };
        return item;
      }).map(item => ({
        ...item,
        percentage: monthlyGrossSales > 0
          ? (item.value / monthlyGrossSales) * 100
          : 0
      }));

      setCostStructure(recalculatedStructure);
    }
  };

  const handleStartDateChange = (e) => setStartDate(e.target.value);
  const handleEndDateChange = (e) => setEndDate(e.target.value);
  const handleClearFilter = () => { setStartDate(''); setEndDate(''); };

  const handleSaveReport = async () => {
    const reportData = {
      startDate,
      endDate,
      monthlyGrossSales,
      averageRecipeCostPercentage,
      costStructure
    };

    try {
      const response = await fetch('/api/save_cost_structure_report.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportData)
      });

      const data = await response.json();

      if (data.success) {
        alert('Reporte guardado correctamente');
        loadReports();
      } else {
        alert('Error al guardar reporte');
      }

    } catch (error) {
      console.error(error);
      alert('Error de conexión');
    }
  };

  const handleLoadReport = (report) => {
    const parsedStructure = JSON.parse(report.report_data);
    setStartDate(report.start_date || '');
    setEndDate(report.end_date || '');
    setMonthlyGrossSales(Number(report.monthly_gross_sales));
    setAverageRecipeCostPercentage(Number(report.average_recipe_cost_percentage));
    setCostStructure(parsedStructure);
  };

  return (
    <div className="mt-6">
      <div className="bg-[#F9EFD7] p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-[#3F1907] mb-3">Estructura de Costos</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-[#3F1907] text-sm font-bold mb-1">Fecha Inicio</label>
            <input type="date" value={startDate} onChange={handleStartDateChange} className="w-full px-3 py-2 border border-[#58735A] rounded-md" />
          </div>

          <div>
            <label className="block text-[#3F1907] text-sm font-bold mb-1">Fecha Fin</label>
            <input type="date" value={endDate} onChange={handleEndDateChange} className="w-full px-3 py-2 border border-[#58735A] rounded-md" />
          </div>

          <div className="flex items-end space-x-2">
            <button onClick={handleClearFilter} className="flex-grow bg-gray-500 text-[#F9EFD7] py-2 px-4 rounded-md">Limpiar Filtro</button>
            <button onClick={handleSaveReport} className="flex-grow bg-blue-600 text-[#F9EFD7] py-2 px-4 rounded-md">Guardar Reporte</button>
          </div>
        </div>

        <div className="flex justify-between items-center mb-6">
          <div className="bg-[#58735A] text-[#F9EFD7] p-3 rounded-md">
            <p>Porcentaje Promedio de Costo de la Receta:</p>
            <p className="text-xl font-bold">{averageRecipeCostPercentage.toFixed(2)}%</p>
          </div>

          <div className="bg-[#58735A] text-[#F9EFD7] p-3 rounded-md">
            <p>Ventas Brutas Mensuales (sin IVA):</p>
            <p className="text-xl font-bold">${monthlyGrossSales.toFixed(2)}</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#58735A]">
            <thead className="bg-[#58735A]">
              <tr>
                <th className="px-4 py-3 text-[#F9EFD7]">Rubros</th>
                <th className="px-4 py-3 text-[#F9EFD7]">Valores</th>
                <th className="px-4 py-3 text-[#F9EFD7]">Porcentaje</th>
                <th className="px-4 py-3 text-[#F9EFD7]">Referencia</th>
              </tr>
            </thead>

            <tbody className="bg-[#F9EFD7]">
              {costStructure.map((item, index) => (
                <tr key={item.name}>
                  <td className="px-4 py-3">{item.name}</td>
                  <td className="px-4 py-3">
                    {item.name === 'Costo de la Mercancía Vendida (CMV)' || item.name === 'Utilidad Neta'
                      ? `$${item.value.toFixed(2)}`
                      : <input type="number" value={item.value} onChange={(e) => handleCostValueChange(index, e.target.value)} className="w-24 px-2 py-1 border border-[#58735A] rounded-md" />}
                  </td>
                  <td className="px-4 py-3">{item.percentage.toFixed(2)}%</td>
                  <td className="px-4 py-3">{item.reference}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-8">
          <h3 className="text-lg font-semibold text-[#3F1907] mb-3">Reportes Guardados</h3>
          <table className="min-w-full divide-y divide-[#58735A]">
            <thead className="bg-[#58735A]">
              <tr>
                <th className="px-4 py-2 text-[#F9EFD7]">Fecha</th>
                <th className="px-4 py-2 text-[#F9EFD7]">Periodo</th>
                <th className="px-4 py-2 text-[#F9EFD7]">Ventas</th>
                <th className="px-4 py-2 text-[#F9EFD7]">Acción</th>
              </tr>
            </thead>
            <tbody className="bg-[#F9EFD7]">
              {reports.map(report => (
                <tr key={report.id}>
                  <td className="px-4 py-2">{new Date(report.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2">{report.start_date} - {report.end_date}</td>
                  <td className="px-4 py-2">${Number(report.monthly_gross_sales).toFixed(2)}</td>
                  <td className="px-4 py-2">
                    <button onClick={() => handleLoadReport(report)} className="bg-green-600 text-white px-3 py-1 rounded">Ver</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CostStructurePage; 